import type { DealBookingType, ProductSelectorType } from '@src/types/OTC';
import {
  isFixedRateLoanDealForm,
  isFloatingRateLoanDealForm,
  isForwardDealForm,
  isOptionDealForm,
  isRepoDealForm,
} from '@src/types/OTC/otcGuards';
import { RepoDirectionEnum } from '@src/types/OTC/otcSharedTypes';
import type { CommissionCalculationTypeEnum } from '@talos/kyoko';
import {
  AllocationValueTypeEnum,
  DateField,
  Field,
  LoanSideEnum,
  MixpanelEvent,
  NumericField,
  OptionTypeEnum,
  PricingModeEnum,
  SelectorField,
  SideEnum,
  Unit,
  createDateOnlyToday,
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isInstantiatedFixedRateLoanSecurity,
  isInstantiatedFloatingRateLoanSecurity,
  isInstantiatedOTCForwardSecurity,
  isInstantiatedOTCOptionSecurity,
  isInstantiatedOTCRepoSecurity,
  isOTCForwardProductTemplate,
  isOTCOptionProductTemplate,
  isOTCRepoProductTemplate,
  type Security,
} from '@talos/kyoko';
import { REPO_MARGIN_PERCENT_DISPLAY_SCALE } from './repoMarginUtils';
import type {
  FixedRateLoanDealTradeForm,
  FloatingRateLoanDealTradeForm,
  ForwardDealTradeForm,
  ManualDealTradeCommonForm,
  ManualDealTradeForm,
  OptionDealTradeForm,
  RepoDealTradeForm,
} from './types';

type ManualDealCommonFormState = Omit<ManualDealTradeCommonForm, 'bookingTypeField'>;

export function dealSubAccountFieldName(bookingType: DealBookingType | undefined): string {
  switch (bookingType) {
    case 'fixedRateLoan':
    case 'floatingRateLoan':
      return 'Loan Sub Account';
    case 'repo':
      return 'Repo Sub Account';
    case 'forward':
    case 'option':
    case undefined:
      return 'Sub Account';
    default: {
      const _exhaustive: never = bookingType;
      return _exhaustive;
    }
  }
}

function getInitialCommonFormState(bookingType: DealBookingType): ManualDealCommonFormState {
  const tradeDate = createDateOnlyToday();
  return {
    templateSymbolField: new SelectorField<Security>({ idProperty: 'Symbol', name: 'Template Symbol' }),
    symbolField: new SelectorField<Security>({ idProperty: 'Symbol', name: 'Symbol' }),
    marketField: new SelectorField<string>({ name: 'Market', isRequired: true }),
    marketAccountField: new SelectorField<string>({ name: 'Market Account', isRequired: true }),
    counterpartyField: new SelectorField<string>({ name: 'Counterparty', isRequired: true }),
    allocationValueTypeField: new Field<AllocationValueTypeEnum>({ value: AllocationValueTypeEnum.Percentage }),
    allocations: [
      {
        subAccountField: new Field<string>({ name: dealSubAccountFieldName(bookingType) }),
        valueField: new NumericField({
          name: 'Allocation amount',
          unit: Unit.Percent,
        }).updateValue('100'),
      },
    ],
    tradeDateField: new Field({
      name: 'Trade Date',
      value: tradeDate,
      isRequired: false,
    }),
    effectiveDateField: new Field({
      name: 'Effective Date',
      value: tradeDate,
      isRequired: false,
    }),
    settlementDateField: new DateField({ name: 'Settlement Date' }),
    expiryDateField: new DateField({ name: 'Expiry Date' }),
  };
}

export function getInitialForwardFormState(): ForwardDealTradeForm {
  const commonState = getInitialCommonFormState('forward');
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'forward' }),
    sideField: new Field<SideEnum>({ value: SideEnum.Buy }),
    unitNotionalField: new NumericField({ name: 'Unit Notional' }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    notionalAmountField: new NumericField({ name: 'Notional Amount' }),
    forwardPriceField: new NumericField({ name: 'Forward Price' }),
    pricingModeField: new Field<PricingModeEnum>({ name: 'Pricing Mode', value: PricingModeEnum.Absolute }),
    indexField: new SelectorField<string>({ name: 'Index' }),
    spreadField: new NumericField({ name: 'Spread', unit: Unit.Bps }),
    commissionBpsField: new NumericField({ name: 'Commission', unit: Unit.Bps }),
    commissionCurrencyField: new SelectorField<string>({ name: 'Commission Currency' }),
    commissionCalculationTypeField: new Field<CommissionCalculationTypeEnum | ''>({
      name: 'Commission Calculation Type',
    }),
    initialMarginCurrencyField: new SelectorField<string>({ name: 'Initial Margin Currency' }),
    haircutPercentField: new NumericField({ name: 'Haircut %', unit: Unit.Percent }),
    initialMarginPercentField: new NumericField({ name: 'Initial Margin %', unit: Unit.Percent }),
    releaseMarginPercentField: new NumericField({ name: 'Release Margin %', unit: Unit.Percent }),
    maintenanceMarginPercentField: new NumericField({ name: 'Maintenance Margin %', unit: Unit.Percent }),
    marginCallPercentField: new NumericField({ name: 'Margin Call %', unit: Unit.Percent }),
    liquidationMarginPercentField: new NumericField({ name: 'Liquidation Margin %', unit: Unit.Percent }),
    notionalWithCurrencyField: {
      value: new NumericField({ name: 'Notional Amount' }),
      currency: new SelectorField<string>({ name: 'Notional Currency' }),
    },
  };
}

export function getInitialFixedRateLoanFormState(): FixedRateLoanDealTradeForm {
  const commonState = getInitialCommonFormState('fixedRateLoan');
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'fixedRateLoan' }),
    loanSideField: new Field<LoanSideEnum>({ value: LoanSideEnum.Lend }),
    unitLoanAmountField: new NumericField({ name: 'Unit Loan Amount' }),
    loanAmountField: new NumericField({ name: 'Loan Amount' }),
    rateField: new NumericField({ name: 'Rate', unit: Unit.Percent }),
    calculatedRateField: new NumericField({ name: 'Calculated Rate', unit: Unit.Percent }),
    paymentPeriodField: new SelectorField<string>({ name: 'Payment Period' }),
    rateSourceField: new SelectorField<string>({ name: 'Rate Source' }),
    loanOriginationFeeSpreadField: new NumericField({ name: 'Loan Origination Fee Spread', unit: Unit.Bps }),
    loanOriginationFeeField: new NumericField({ name: 'Loan Origination Fee' }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    pricingModeField: new Field<PricingModeEnum>({ name: 'Pricing Mode', value: PricingModeEnum.Absolute }),
    spreadField: new NumericField({ name: 'Spread', unit: Unit.Bps }),
    thresholdsMinimumField: new NumericField({ name: 'Minimum LTV', unit: Unit.Percent }),
    thresholdsMarginCallField: new NumericField({ name: 'Margin Call LTV', unit: Unit.Percent }),
    thresholdsLiquidationField: new NumericField({ name: 'Liquidation LTV', unit: Unit.Percent }),
    thresholdsCollateralReleaseField: new NumericField({ name: 'Collateral Release LTV', unit: Unit.Percent }),
    initialCollateralAmountField: new NumericField({ name: 'Initial Collateral Amount' }),
    initialCollateralPercentField: new NumericField({ name: 'Initial Collateral Percent', unit: Unit.Percent }),
    collateralSubAccountField: new Field<string>({ name: 'Collateral Sub Account' }),
  };
}

export function getInitialOptionFormState(): OptionDealTradeForm {
  const commonState = getInitialCommonFormState('option');
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'option' }),
    sideField: new Field<SideEnum>({ value: SideEnum.Buy }),
    strikePriceField: new NumericField({ name: 'Strike Price' }),
    optionTypeField: new Field<OptionTypeEnum>({ value: OptionTypeEnum.Call }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    notionalAmountField: new NumericField({ name: 'Notional Amount' }),
    notionalWithCurrencyField: {
      value: new NumericField({ name: 'Notional Amount' }),
      currency: new SelectorField<string>({ name: 'Notional Currency' }),
    },
    premiumField: new NumericField({ name: 'Premium' }),
    commissionBpsField: new NumericField({ name: 'Commission', unit: Unit.Bps }),
    commissionCurrencyField: new SelectorField<string>({ name: 'Commission Currency' }),
    initialMarginCurrencyField: new SelectorField<string>({ name: 'Initial Margin Currency' }),
    haircutPercentField: new NumericField({ name: 'Haircut %', unit: Unit.Percent }),
    initialMarginPercentField: new NumericField({ name: 'Initial Margin %', unit: Unit.Percent }),
    releaseMarginPercentField: new NumericField({ name: 'Release Margin %', unit: Unit.Percent }),
    maintenanceMarginPercentField: new NumericField({ name: 'Maintenance Margin %', unit: Unit.Percent }),
    marginCallPercentField: new NumericField({ name: 'Margin Call %', unit: Unit.Percent }),
    liquidationMarginPercentField: new NumericField({ name: 'Liquidation Margin %', unit: Unit.Percent }),
  };
}

export function getInitialFloatingRateLoanFormState(): FloatingRateLoanDealTradeForm {
  const commonState = getInitialCommonFormState('floatingRateLoan');
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'floatingRateLoan' }),
    loanSideField: new Field<LoanSideEnum>({ value: LoanSideEnum.Lend }),
    unitLoanAmountField: new NumericField({ name: 'Unit Loan Amount' }),
    loanAmountField: new NumericField({ name: 'Loan Amount' }),
    calculatedRateField: new NumericField({ name: 'Calculated Rate', unit: Unit.Percent }),
    paymentPeriodField: new SelectorField<string>({ name: 'Payment Period' }),
    rateSourceField: new SelectorField<string>({ name: 'Rate Source' }),
    spreadField: new NumericField({ name: 'Spread', unit: Unit.Bps }),
    loanOriginationFeeSpreadField: new NumericField({ name: 'Loan Origination Fee Spread', unit: Unit.Bps }),
    loanOriginationFeeField: new NumericField({ name: 'Loan Origination Fee' }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    thresholdsMinimumField: new NumericField({ name: 'Minimum LTV', unit: Unit.Percent }),
    thresholdsMarginCallField: new NumericField({ name: 'Margin Call LTV', unit: Unit.Percent }),
    thresholdsLiquidationField: new NumericField({ name: 'Liquidation LTV', unit: Unit.Percent }),
    thresholdsCollateralReleaseField: new NumericField({ name: 'Collateral Release LTV', unit: Unit.Percent }),
    initialCollateralAmountField: new NumericField({ name: 'Initial Collateral Amount' }),
    initialCollateralPercentField: new NumericField({ name: 'Initial Collateral Percent', unit: Unit.Percent }),
    collateralSubAccountField: new Field<string>({ name: 'Collateral Sub Account' }),
  };
}

export function getInitialRepoFormState(): RepoDealTradeForm {
  const commonState = getInitialCommonFormState('repo');
  return {
    ...commonState,
    bookingTypeField: new Field<DealBookingType>({ value: 'repo' }),
    directionField: new Field<RepoDirectionEnum>({ value: RepoDirectionEnum.ReverseRepo }),
    primeBrokerField: new SelectorField<string>({ name: 'Prime Broker', isRequired: true }),
    quantityField: new NumericField({ name: 'Quantity' }),
    quantityCurrencyField: new SelectorField<string>({ name: 'Quantity Currency', value: 'Units' }),
    rateField: new NumericField({ name: 'Rate', unit: Unit.Percent }),
    priceSourceField: new Field<string>({ name: 'Price Source', isDisabled: true }),
    referencePriceField: new NumericField({ name: 'Reference Price', isDisabled: true }),
    referenceTimeField: new Field<string>({ name: 'Reference Time', isDisabled: true }),
    haircutPctField: new NumericField({ name: 'Haircut %', unit: Unit.Percent }),
    marginReleasePctField: new NumericField({
      name: 'Margin Release %',
      unit: Unit.Percent,
      scale: REPO_MARGIN_PERCENT_DISPLAY_SCALE,
    }),
    maintenanceMarginPctField: new NumericField({
      name: 'Maintenance Margin %',
      unit: Unit.Percent,
      scale: REPO_MARGIN_PERCENT_DISPLAY_SCALE,
    }),
    marginCallPctField: new NumericField({
      name: 'Margin Call %',
      unit: Unit.Percent,
      scale: REPO_MARGIN_PERCENT_DISPLAY_SCALE,
    }),
    closeOutMarginPctField: new NumericField({
      name: 'Close-out %',
      unit: Unit.Percent,
      scale: REPO_MARGIN_PERCENT_DISPLAY_SCALE,
    }),
    collateralSubAccountField: new Field<string>({ name: 'Collateral Sub Account' }),
  };
}

export const manualDealProductLogic = {
  forward: {
    productLabel: 'OTC forward',
    productTemplateGroup: 'Forward',
    createInitialForm: getInitialForwardFormState,
    submitMixpanelEvent: MixpanelEvent.BookManualOTCDeal,
    createProductMixpanelEvent: MixpanelEvent.CreateNewOTC,
    isProductTemplate: isOTCForwardProductTemplate,
    isInstantiatedSecurity: isInstantiatedOTCForwardSecurity,
    isDealForm: isForwardDealForm,
  },
  fixedRateLoan: {
    productLabel: 'fixed rate loan',
    productTemplateGroup: 'Fixed Rate',
    createInitialForm: getInitialFixedRateLoanFormState,
    submitMixpanelEvent: MixpanelEvent.BookManualLoan,
    createProductMixpanelEvent: MixpanelEvent.CreateNewLoan,
    isProductTemplate: isFixedRateLoanProductTemplate,
    isInstantiatedSecurity: isInstantiatedFixedRateLoanSecurity,
    isDealForm: isFixedRateLoanDealForm,
  },
  option: {
    productLabel: 'OTC option',
    productTemplateGroup: 'Vanilla Option',
    createInitialForm: getInitialOptionFormState,
    submitMixpanelEvent: MixpanelEvent.BookManualOTCDeal,
    createProductMixpanelEvent: MixpanelEvent.CreateNewOTC,
    isProductTemplate: isOTCOptionProductTemplate,
    isInstantiatedSecurity: isInstantiatedOTCOptionSecurity,
    isDealForm: isOptionDealForm,
  },
  floatingRateLoan: {
    productLabel: 'floating rate loan',
    productTemplateGroup: 'Floating Rate',
    createInitialForm: getInitialFloatingRateLoanFormState,
    submitMixpanelEvent: MixpanelEvent.BookManualLoan,
    createProductMixpanelEvent: MixpanelEvent.CreateNewLoan,
    isProductTemplate: isFloatingRateLoanProductTemplate,
    isInstantiatedSecurity: isInstantiatedFloatingRateLoanSecurity,
    isDealForm: isFloatingRateLoanDealForm,
  },
  repo: {
    productLabel: 'repo',
    productTemplateGroup: 'Repo',
    createInitialForm: getInitialRepoFormState,
    submitMixpanelEvent: MixpanelEvent.BookManualOTCDeal,
    createProductMixpanelEvent: MixpanelEvent.CreateNewRepo,
    isProductTemplate: isOTCRepoProductTemplate,
    isInstantiatedSecurity: isInstantiatedOTCRepoSecurity,
    isDealForm: isRepoDealForm,
  },
} satisfies Record<
  DealBookingType,
  {
    productLabel: string;
    productTemplateGroup: string;
    createInitialForm: () => ManualDealTradeForm;
    submitMixpanelEvent: MixpanelEvent;
    createProductMixpanelEvent: MixpanelEvent;
    isProductTemplate: (security: Security | undefined) => boolean;
    isInstantiatedSecurity: (security: Security | undefined) => boolean;
    isDealForm: (form: ManualDealTradeForm) => boolean;
  }
>;

export function getInitialFormState(bookingType: DealBookingType): ManualDealTradeForm {
  return manualDealProductLogic[bookingType].createInitialForm();
}

/** Fallback group for securities that do not match any known product template guard. */
const OTHER_GROUP = 'Other';

export const manualDealProductTemplateGroupSortOrder = {
  loan: [
    manualDealProductLogic.fixedRateLoan.productTemplateGroup,
    manualDealProductLogic.floatingRateLoan.productTemplateGroup,
    OTHER_GROUP,
  ],
  otc: [
    manualDealProductLogic.forward.productTemplateGroup,
    manualDealProductLogic.option.productTemplateGroup,
    OTHER_GROUP,
  ],
  repo: [manualDealProductLogic.repo.productTemplateGroup, OTHER_GROUP],
} satisfies Record<ProductSelectorType, string[]>;

const manualDealProductTemplateGroupBuckets = {
  loan: ['fixedRateLoan', 'floatingRateLoan'],
  otc: ['forward', 'option'],
  repo: ['repo'],
} as const satisfies Record<ProductSelectorType, readonly DealBookingType[]>;

export function getManualDealProductTemplateGroup(security: Security, productType: ProductSelectorType): string {
  const bookingType = manualDealProductTemplateGroupBuckets[productType].find(type =>
    manualDealProductLogic[type].isProductTemplate(security)
  );
  return bookingType ? manualDealProductLogic[bookingType].productTemplateGroup : OTHER_GROUP;
}
